import {Injectable} from '@angular/core';
import {ApiClientService} from './api-client.service';
import {AuthenticationService} from './authentication.service';
import {IncidentReport} from '../types/models';
import {UrlService} from './url.service';
import {EmptyRowService} from './empty-row.service';
import {ConstantsService} from './constants.service';
import {AccountPermissionsService} from './account-permissions.service';
import {HistoryItem} from '../views/directory-item/activity/history-item.class';
import {filter, map, tap} from 'rxjs/operators';
import {Observable} from 'rxjs/Observable';
import {Attachment} from '../types/email-data.types';
import {EmailService} from './email.service';
import {createInitialNetworkEntity, resolveNetworkEntity} from './helper-functions';
import {of} from 'rxjs/observable/of';
import {BehaviorSubject} from 'rxjs/BehaviorSubject';

declare const toastr: any;
declare const swal: any;
declare const $: any;

@Injectable()
export class IncidentReportsService {
    isIncidentReportModalShow = false;
    isSendIncidentReportModalShow = false;
    showDetailedView = false;

    incidentReportInIncidentReportsModal: IncidentReport;

    data = {
        allIncidentReports: {
            get: '/v1/incident_report.json?expand=building,suite,resident,incidentReportType,createdBy,attachments',
            data: [],
            total: null
        },
    };
    tabId = 'allIncidentReports';
    spinnerShow = true;
    showServiceUnavailable = false;
    buildings: Array<any> = [];
    buildingsForRead: Array<any> = [];
    buildingsForUpdate: Array<any> = [];
    serviceName: string = 'inc_report';
    historyModalIncidentReport: IncidentReport;
    selectedItemData = null;
    activities = [];
    historyLoading = false;
    incidentTypes = [];
    commentsLoading = false;
    isCommentsLoaded: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    commentsListLoaded = this.isCommentsLoaded.asObservable();

    constructor(
        public api: ApiClientService,
        public es: EmailService,
        public urlService: UrlService,
        public authenticationService: AuthenticationService,
        public emptyRowService: EmptyRowService,
        public account: AccountPermissionsService
    ) {
        this.api.onLogout.subscribe(() => this.buildings = []);
    }

    getData(scrollToTop?) {
        this.getDataByQueryParams(scrollToTop).subscribe();
    }

    getDataByQueryParams(scrollToTop?) {
        this.showServiceUnavailable = false;
        this.data[this.tabId].total = null;
        const tabId = this.urlService.queryParams['tab'];
        const url = this.data[tabId].get;
        const queryParams = this.urlService.getQueryParamsString();

        return this.api.getJson('domainIncidentReports', `${url}${queryParams}`)
            .pipe(
                createInitialNetworkEntity(),
                resolveNetworkEntity(
                    item => {
                        if (item.building && item.building.bdId) {
                            item.building.id = item.building.bdId;
                        }
                        return IncidentReport.fromExistingData(this.account.addPermissionsToEntity(item, this.serviceName));
                    }
                ),
                tap(({loading}) => this.spinnerShow = loading),
                tap(
                    (body) => {
                        this.data[tabId].data = body.entities;
                        this.data[tabId].total = body.total;
                        this.emptyRowService.fillEmptyRow();
                        if ($('.table')[0] && scrollToTop)
                            $('.table')[0].scrollIntoView();
                    },
                    (error) => {
                        this.showServiceUnavailable = true;
                        this.spinnerShow = false;
                        this.api.errorHandler(error, 'Failed get Incident Reports');
                    }
                ),
                filter(({finished}) => finished),
                tap(() => {
                    if (this.selectedItemData) {
                        this.selectedItemData = this.data.allIncidentReports.data.find(si => si.id == this.selectedItemData.id);
                        if (this.selectedItemData) {
                            this.selectedItemData.checked = true;
                            this.getComment(this.selectedItemData.id);
                        } else {
                            this.showDetailedView = false;
                        }
                    }
                })
            );
    }

    getBuildings(): void {
        this.getBuildingsRequest().subscribe();
    }

    getBuildingsRequest() {
        if (this.buildings.length) return of(this.buildings);
        const url = `/v1/buildings/short.json?per-page=10000&order-by=name|asc`;
        return this.api.getJson('domainBD', url)
            .pipe(
                tap(
                    (res) => {
                        this.buildings = res.entities;
                        this.getFilteredBuildings();
                    },
                    (error) => this.api.errorHandler(error, 'Failed get buildings')
                )
            );
    }

    resetData() {
        for (const item in this.data) {
            this.data[item].data = [];
            this.data[item].total = null;
        }
    }

    editIncidentReport(incidentReport: IncidentReport) {
        this.isIncidentReportModalShow = true;
        this.incidentReportInIncidentReportsModal = Object.assign({}, incidentReport);
    }

    deleteIncidentReport(id: number): void {
        if (!id) return;
        swal(ConstantsService.getSwalConfig('Delete Incident Report'),
            () => {
                const url = `/v1/incident_report/${id}.json`;
                this.api.remove('domainIncidentReports', url).subscribe(
                    () => {
                        toastr.success('Incident Report has been deleted.');
                        if (this.data.allIncidentReports.data.length - 1 == 0 && this.urlService.queryParams.page != '1')
                            this.urlService.queryParams.page = (parseInt(this.urlService.queryParams.page) - 1).toString();
                        this.getData();
                    },
                    (error) => this.api.errorHandler(error, 'Delete Incident Report failed')
                );
            }
        );
    }

    /**
     * Receives Incident Report
     * activity details
     *
     * @param incidentReportId
     */
    public getIncidentReportHistory(incidentReportId): Observable<HistoryItem[]> {
        const url = `/v1/history.json?per-page=10000&order-by=loggedAt|desc&objectId=${incidentReportId}`;

        return this.api.get('domainIncidentReports', url).pipe(
            map(
                response => response.json().map(item => new HistoryItem(item))
            )
        );
    }

    /**
     * @param incidentReport
     */
    getPDF(incidentReport: IncidentReport) {
        const url = `/v1/incident-reports-pdf/${incidentReport.id}.json`;
        return this.api.get('domainIncidentReports', url).subscribe(
            res => {
                const body = JSON.parse(res._body);
                window.open(body.path);
            }, error => toastr.error('Failed to get service request PDF.')
        );
    }

    canEdit(buildingId?: number) {
        return this.account.canUpdate(this.serviceName, buildingId);
    }

    canDelete(buildingId?: number) {
        return this.account.canDelete(this.serviceName, buildingId);
    }

    getFilteredBuildings() {
        this.buildingsForRead = this.account.getBuildingsForRead(this.buildings, this.serviceName);
        this.buildingsForUpdate = this.account.getBuildingsForUpdate(this.buildings, this.serviceName);
    }

    showHistory(report: IncidentReport) {
        this.historyModalIncidentReport = report;
    }

    toggleDetailedView(data, state: boolean): void {
        this.data.allIncidentReports.data.forEach(item => item.checked = false);
        this.selectedItemData = data ? data : null;
        this.showDetailedView = state;
        if (this.selectedItemData) {
            this.selectedItemData.checked = true;
            this.getComment(this.selectedItemData.id);
            this.getActivity();
            for (const field in this.selectedItemData.formFields)
                this.selectedItemData.formFields[field].editMode = false;
        }
    }

    getComment(id) {
        this.commentsLoading = true;
        const user = this.authenticationService.user;
        const url = `/v1/comment.json?report_id=${id}&expand=createdBy`;
        this.api.get('domainIncidentReports', url).subscribe(
            res => {
                this.commentsLoading = false;
                let body = JSON.parse(res._body).entities || [];

                if (body.length) {
                    body = body.map(item => ({
                        ...item,
                        editMode: false,
                        isCanEdit: user.id == +item.createdBy.bdId
                    }));
                    this.selectedItemData.status = 'draft';
                }
                const data = this.data.allIncidentReports.data.find(item => item.id == id);
                data.entries = body;
                this.isCommentsLoaded.next(true);
            }, error => {
                toastr.error('Failed to get service request comments.');
                this.commentsLoading = false;
            }
        );
    }

    getActivity() {
        this.historyLoading = true;
        this.getIncidentReportHistory(this.selectedItemData.id).subscribe(
            items => {
                this.activities = [];
                this.activities = items;
                this.historyLoading = false;
            },
            error => {
                this.historyLoading = false;
                this.api.errorHandler(error, 'Failed to get incident report history.');
            }
        );
    }

    getIncidentTypesRequest() {
        const url = `/v1/incident-reports-types.json?per-page=1000`;
        return this.api.getJson('domainIncidentReports', url)
            .pipe(
                createInitialNetworkEntity(),
                resolveNetworkEntity(type => ({id: type.id, name: type.name})),
                map(response => {
                    return {
                        ...response,
                        entities: [
                            ...response.entities,
                            {id: 0, name: 'Other'}
                        ]
                    };
                }),
                tap(
                    res => this.incidentTypes = res.entities,
                    error => this.api.errorHandler(error, 'Failed get types')
                )
            );
    }

    showAttachment(attachment) {
        const attachmentAsObject = new Attachment(attachment.name, 0, attachment.link);
        if (attachmentAsObject.isImage()) {
            this.es.attachmentInViewAttachmentModal = attachmentAsObject;
            this.es.isViewAttachmentModalShow = true;
        } else if (attachmentAsObject.isNeedViewer()) {
            attachmentAsObject.openViewer();
        } else {
            attachmentAsObject.downloadAttachment();
        }
    }

    fileIsImage(attachment) {
        const attachmentAsObject = new Attachment(attachment.name, 0, attachment.link);
        return attachmentAsObject.isImage();
    }

    getFileExt(filePath: string): string {
        return filePath.split('.').pop();
    }
}



// WEBPACK FOOTER //
// /app/src/app/services/incident-reports.service.ts